'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { BlocksContent } from '@strapi/blocks-react-renderer';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Link from 'next/link';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { Recaptcha, RecaptchaHandle } from '@/components/Forms/Recaptcha';
import { trackEvent } from '@/lib/analytics/gtm';

dayjs.extend(customParseFormat);

/** Shown to the user; the picker and placeholder both use it. */
const DOB_FORMAT = 'DD/MM/YYYY';
/** Held in form state and sent to the broker, which expects an ISO date. */
const ISO_DATE = 'YYYY-MM-DD';

const DEFAULT_PHONE = '1300 668 843';

/**
 * Compact type scale. The site's base typography is sized for page copy, which is far too
 * large inside a form, so labels, inputs and helper text are set explicitly.
 */
const LABEL_SIZE = '0.8125rem';
const INPUT_SIZE = '0.9375rem';
const HELPER_SIZE = '0.75rem';

const demoRequestSchema = z.object({
  name: z.string().trim().min(1, 'Full name is required'),
  email: z.string().trim().min(1, 'Email is required').email('Valid email is required'),
  dob: z
    .string()
    .trim()
    .min(1, 'Date of birth is required')
    .refine((value) => dayjs(value, ISO_DATE, true).isValid(), 'Select a valid date of birth')
    .refine(
      (value) => dayjs(value, ISO_DATE, true).isBefore(dayjs(), 'day'),
      'Date of birth must be in the past'
    ),
  mobile: z
    .string()
    .trim()
    .min(1, 'Mobile is required')
    .regex(/^(\+61|0)4\d{2}[-\s]?\d{3}[-\s]?\d{3}$/, 'Valid Australian mobile required'),
  aggregatorId: z.string().optional(),
});

type DemoRequestFormValues = z.infer<typeof demoRequestSchema>;

type Aggregator = { id: string; name: string };

/** Broker validation errors come back keyed by database column. */
const BROKER_FIELD_MAP: Record<string, keyof DemoRequestFormValues> = {
  name: 'name',
  email: 'email',
  dob: 'dob',
  mobile: 'mobile',
  aggregator_id: 'aggregatorId',
};

interface FormGetStartedProps {
  handleClose(): void;

  content:
    | {
        contactUsNumber: string;
        contactUsPrompt: string;
        contactUsFormHeader: BlocksContent;
      }
    | undefined;
}

/**
 * Demo request modal opened from the "Talk to Us" nav button. Submits to /api/demo-request,
 * which proxies to the broker platform (demo_partners + CS Slack notification).
 */
export function FormGetStarted(props: FormGetStartedProps) {
  const theme = useTheme();
  const labelId = useId();

  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  const recaptchaRef = useRef<RecaptchaHandle>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [recaptchaFailed, setRecaptchaFailed] = useState(false);
  const [aggregators, setAggregators] = useState<Aggregator[]>([]);
  const [isSubmitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<DemoRequestFormValues>({
    resolver: zodResolver(demoRequestSchema),
    defaultValues: { name: '', email: '', dob: '', mobile: '', aggregatorId: '' },
  });

  useEffect(() => {
    let cancelled = false;

    fetch('/api/demo-request/', { method: 'GET', headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : { aggregators: [] }))
      .then((data) => {
        if (!cancelled) setAggregators(data?.aggregators ?? []);
      })
      .catch((error) => {
        // A missing aggregator list must not block the form - the field is optional.
        console.error('[DEMO_REQUEST] Unable to load aggregators:', error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const resetRecaptcha = useCallback(() => {
    recaptchaRef.current?.reset();
    setRecaptchaToken(null);
  }, []);

  const onSubmit = async (values: DemoRequestFormValues) => {
    setSubmitError(null);

    try {
      const response = await fetch('/api/demo-request/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          dob: values.dob,
          mobile: values.mobile,
          aggregatorId: values.aggregatorId || undefined,
          recaptchaToken,
        }),
      });

      const data = await response.json();

      if (data?.success === true) {
        setSubmitted(true);
        trackEvent({ feature: 'demo-request-modal', action: 'form_submit', isKeyEvent: true });
        return;
      }

      // Errors naming a known field are shown against that field; anything else above the form.
      const brokerErrors: { field?: string; error?: string }[] = data?.errors ?? [];
      let handledAsFieldError = false;

      brokerErrors.forEach((item) => {
        const field = item.field ? BROKER_FIELD_MAP[item.field] : undefined;
        if (field && item.error) {
          setError(field, { message: item.error });
          handledAsFieldError = true;
        }
      });

      const general = brokerErrors.find((item) => !item.field || !BROKER_FIELD_MAP[item.field]);
      if (general || !handledAsFieldError) {
        setSubmitError(general?.error || 'Something went wrong, please try again.');
      }
      resetRecaptcha();
    } catch (error) {
      console.error('[DEMO_REQUEST] Submission failed:', error);
      setSubmitError('Something went wrong, please try again.');
      resetRecaptcha();
    }
  };

  // CMS strings arrive as '' rather than undefined when unset, so ?? isn't enough.
  const phone = props.content?.contactUsNumber || DEFAULT_PHONE;
  const prompt = props.content?.contactUsPrompt || 'Or want to talk to someone now?';
  const cmsHeader = props.content?.contactUsFormHeader;
  const hasCmsHeader = Array.isArray(cmsHeader) && cmsHeader.length > 0;

  const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: INPUT_SIZE },
    '& .MuiOutlinedInput-input': { py: 1.25, px: 1.5, fontSize: INPUT_SIZE },
    '& .MuiFormHelperText-root': {
      fontSize: HELPER_SIZE,
      lineHeight: 1.4,
      mt: 0.5,
      mx: 0.25,
    },
  };

  const requiredMark = (
    <Box component="span" sx={{ color: theme.palette.brand.error, ml: 0.25 }} aria-hidden="true">
      *
    </Box>
  );

  // Disabled until the captcha is solved. Neither a failed widget nor a missing site key
  // unlocks the button - the route rejects an empty token, so submitting could only ever fail.
  const canSubmit = !isSubmitting && !!recaptchaToken;

  return (
    <Box
      data-component="FormGetStarted"
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 460,
        maxWidth: 'calc(100vw - 2rem)',
        maxHeight: 'calc(100vh - 2rem)',
        overflowY: 'auto',
        backgroundColor: theme.palette.common.white,
        borderRadius: '20px',
        p: { xs: 3, sm: 4 },
        boxShadow: '0 24px 64px rgba(32, 11, 66, 0.24)',
        outline: 'none',
        a: { color: theme.palette.brand.primary },
      }}
    >
      <IconButton
        onClick={props.handleClose}
        aria-label="Close"
        size="small"
        sx={{ position: 'absolute', top: 12, right: 12, color: theme.palette.purple[650] }}
      >
        <CloseOutlinedIcon fontSize="small" />
      </IconButton>

      <Box sx={{ mb: 3, pr: 4 }}>
        {hasCmsHeader ? (
          <GlobalBlockRenderer content={cmsHeader} />
        ) : (
          <>
            <Typography
              component="h2"
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                lineHeight: 1.25,
                color: theme.palette.common.black,
              }}
            >
              Book a platform demo
            </Typography>
            <Typography
              sx={{ mt: 1, fontSize: '0.875rem', lineHeight: 1.5, color: 'text.secondary' }}
            >
              Our team will walk you through the Lend Platform and answer your questions.
            </Typography>
          </>
        )}
      </Box>

      {isSubmitted ? (
        <Alert severity="success" sx={alertSx(theme)}>
          Thanks, your demo request has been received. Our team will be in touch shortly to arrange
          a time.
        </Alert>
      ) : (
        <>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}
          >
            {submitError && (
              <Alert severity="error" sx={alertSx(theme)}>
                {submitError}
              </Alert>
            )}

            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Box>
                  <FormLabel htmlFor={`${labelId}-name`} sx={labelSx(theme)}>
                    Full name{requiredMark}
                  </FormLabel>
                  <TextField
                    {...field}
                    id={`${labelId}-name`}
                    fullWidth
                    placeholder="John Smith"
                    autoComplete="name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    sx={fieldSx}
                  />
                </Box>
              )}
            />

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Box>
                  <FormLabel htmlFor={`${labelId}-email`} sx={labelSx(theme)}>
                    Email{requiredMark}
                  </FormLabel>
                  <TextField
                    {...field}
                    id={`${labelId}-email`}
                    fullWidth
                    type="email"
                    placeholder="john@brokerage.com.au"
                    autoComplete="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    sx={fieldSx}
                  />
                </Box>
              )}
            />

            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}
            >
              <Controller
                name="dob"
                control={control}
                render={({ field }) => (
                  <Box>
                    <FormLabel htmlFor={`${labelId}-dob`} sx={labelSx(theme)}>
                      Date of birth{requiredMark}
                    </FormLabel>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        // Field state is the ISO string the broker wants; the picker shows DD/MM/YYYY.
                        value={field.value ? dayjs(field.value, ISO_DATE) : null}
                        onChange={(value) =>
                          field.onChange(value && value.isValid() ? value.format(ISO_DATE) : '')
                        }
                        format={DOB_FORMAT}
                        disableFuture
                        openTo="year"
                        views={['year', 'month', 'day']}
                        minDate={dayjs('1920-01-01')}
                        enableAccessibleFieldDOMStructure={false}
                        slotProps={{
                          textField: {
                            id: `${labelId}-dob`,
                            fullWidth: true,
                            onBlur: field.onBlur,
                            placeholder: DOB_FORMAT,
                            error: !!errors.dob,
                            helperText: errors.dob?.message,
                            sx: fieldSx,
                          },
                          openPickerButton: { size: 'small' },
                          desktopPaper: { sx: pickerPaperSx(theme) },
                          mobilePaper: { sx: pickerPaperSx(theme) },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                )}
              />

              <Controller
                name="mobile"
                control={control}
                render={({ field }) => (
                  <Box>
                    <FormLabel htmlFor={`${labelId}-mobile`} sx={labelSx(theme)}>
                      Mobile{requiredMark}
                    </FormLabel>
                    <TextField
                      {...field}
                      id={`${labelId}-mobile`}
                      fullWidth
                      type="tel"
                      placeholder="0400 000 000"
                      autoComplete="tel"
                      error={!!errors.mobile}
                      helperText={errors.mobile?.message}
                      sx={fieldSx}
                    />
                  </Box>
                )}
              />
            </Box>

            <Controller
              name="aggregatorId"
              control={control}
              render={({ field }) => (
                <Box>
                  <FormLabel htmlFor={`${labelId}-aggregator`} sx={labelSx(theme)}>
                    Aggregator (optional)
                  </FormLabel>
                  <Select
                    {...field}
                    id={`${labelId}-aggregator`}
                    fullWidth
                    displayEmpty
                    disabled={aggregators.length === 0}
                    // The theme sets MuiPaper to `background: none` with 2rem padding, which
                    // leaves the dropdown transparent - the menu paper needs styling explicitly.
                    MenuProps={{
                      PaperProps: {
                        elevation: 4,
                        sx: {
                          p: 0,
                          mt: 0.5,
                          maxHeight: 280,
                          borderRadius: '10px',
                          backgroundColor: theme.palette.common.white,
                          border: `1px solid ${theme.palette.purple[300]}`,
                          boxShadow: '0 8px 24px rgba(32, 11, 66, 0.12)',
                          '& .MuiMenuItem-root': {
                            py: 0.875,
                            px: 1.5,
                            fontSize: INPUT_SIZE,
                            lineHeight: 1.4,
                            whiteSpace: 'normal',
                            '&:hover': { backgroundColor: theme.palette.purple[100] },
                            '&.Mui-selected': {
                              backgroundColor: theme.palette.purple[200],
                              color: theme.palette.brand.primary,
                            },
                          },
                        },
                      },
                    }}
                    renderValue={(selected) =>
                      aggregators.find((aggregator) => aggregator.id === selected)?.name ?? (
                        <Box component="span" sx={{ color: theme.palette.purple[500] }}>
                          Select your aggregator
                        </Box>
                      )
                    }
                    sx={{
                      borderRadius: '10px',
                      fontSize: INPUT_SIZE,
                      backgroundColor: theme.palette.common.white,
                      '& .MuiSelect-select': { py: 1.25, px: 1.5, fontSize: INPUT_SIZE },
                    }}
                  >
                    <MenuItem value="">
                      <em>No aggregator</em>
                    </MenuItem>
                    {aggregators.map((aggregator) => (
                      <MenuItem key={aggregator.id} value={aggregator.id}>
                        {aggregator.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
            />

            {siteKey && !recaptchaFailed && (
              <Recaptcha
                ref={recaptchaRef}
                siteKey={siteKey}
                onChange={setRecaptchaToken}
                onError={() => setRecaptchaFailed(true)}
                sx={{ mt: 0.5 }}
              />
            )}

            {/* A missing site key leaves no widget to solve, so say so rather than showing a
                permanently disabled button with no explanation. */}
            {(!siteKey || recaptchaFailed) && (
              <Alert severity="warning" sx={alertSx(theme)}>
                Verification couldn&apos;t load, so the form can&apos;t be submitted right now.
                Please refresh the page, or call us on{' '}
                <Link href={`tel:${phone.replaceAll(' ', '')}`}>{phone}</Link>.
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={!canSubmit}
              sx={{ mt: 0.5, fontSize: '1rem' }}
            >
              {isSubmitting ? 'Sending...' : 'Request a demo'}
            </Button>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: '0.8125rem',
                color: 'text.secondary',
              }}
            >
              <CalendarMonthOutlinedIcon
                sx={{ fontSize: '1rem', color: theme.palette.brand.green }}
              />
              We&apos;ll be in touch to arrange a time
            </Typography>

            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
              {prompt} <Link href={`tel:${phone.replaceAll(' ', '')}`}>Call {phone}</Link>
            </Typography>

            <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.5, color: 'text.secondary' }}>
              By requesting a demo you agree to our{' '}
              <Link href="https://www.lend.com.au/terms">terms &amp; conditions</Link> and{' '}
              <Link href="https://www.lend.com.au/privacy">privacy policy</Link>.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}

const labelSx = (theme: Theme) => ({
  display: 'block',
  mb: 0.5,
  fontSize: LABEL_SIZE,
  fontWeight: 600,
  lineHeight: 1.4,
  color: theme.palette.common.black,
  '&.Mui-focused': { color: theme.palette.common.black },
});

/** Alerts inherit the theme's `background: none` + 2rem Paper padding, so reset both. */
const alertSx = (theme: Theme) => ({
  py: 1,
  px: 1.5,
  borderRadius: '10px',
  fontSize: HELPER_SIZE,
  alignItems: 'center',
  '& .MuiAlert-message': { fontSize: HELPER_SIZE, py: 0.25 },
  '&.MuiAlert-standardError': { backgroundColor: '#FDECEC' },
  '&.MuiAlert-standardWarning': { backgroundColor: '#FFF4E5' },
  '&.MuiAlert-standardSuccess': { backgroundColor: theme.palette.brand.greenLight },
});

const pickerPaperSx = (theme: Theme) => ({
  p: 0,
  mt: 0.5,
  borderRadius: '12px',
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.purple[300]}`,
  boxShadow: '0 8px 24px rgba(32, 11, 66, 0.12)',
  '& .MuiPickersCalendarHeader-label': { fontSize: '0.9375rem', fontWeight: 600 },
  '& .MuiDayCalendar-weekDayLabel': { fontSize: '0.75rem' },
  '& .MuiPickersDay-root': { fontSize: '0.8125rem' },
  '& .MuiPickersYear-yearButton, & .MuiPickersMonth-monthButton': { fontSize: '0.8125rem' },
});
